import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/auth'
import { userService, watchlistService } from '@/services'

type WatchlistAccount = {
  id: string
  username: string
  full_name?: string
}

type WatchlistData = {
  id: string
  name: string
  total_accounts: number
  accounts: WatchlistAccount[]
  performance_insights?: { top_posts?: Array<any> }
}

export function useCompetitorWatchlist() {
  const { isAuthenticated } = useAuth()

  const { data: profileResp, isLoading: loadingProfile, refetch: refetchProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: () => userService.getProfile(),
    enabled: isAuthenticated,
    staleTime: 60_000,
    refetchOnMount: true, // Refetch once when component mounts
  })

  const watchlistId = useMemo(() => {
    return profileResp?.profile?.CompetitorList
  }, [profileResp])

  const { data: watchlistData, isLoading: loadingWatchlist, error, refetch: refetchWatchlist } = useQuery<WatchlistData | null>({
    queryKey: ['competitor-watchlist', watchlistId],
    queryFn: async () => {
      if (!watchlistId) return null
      return await watchlistService.getWatchlist(watchlistId)
    },
    enabled: isAuthenticated && Boolean(watchlistId),
    staleTime: 60_000,
    refetchOnMount: true, // Refetch once when component mounts
  })

  const topPosts = (watchlistData as any)?.performance_insights?.top_posts as Array<any> | undefined

  return {
    loading: loadingProfile || loadingWatchlist,
    hasWatchlist: Boolean(watchlistId && watchlistData && !error),
    watchlistId,
    watchlist: watchlistData || undefined,
    topPosts: topPosts || [],
    refetch: async () => {
      // Refetch both profile and watchlist data
      await Promise.all([
        loadingProfile ? Promise.resolve() : refetchProfile?.(),
        loadingWatchlist ? Promise.resolve() : refetchWatchlist?.()
      ])
    }
  }
}


